import { type AppChannel, isFramedWidgetKind, type SlotAssignmentView, type SlotId } from '@luminovo/apps-types';
import { getToken } from '@luminovo/auth';

import { SlotAssignmentsResponseSchema } from './slotAssignmentsSchema';

export interface FetchSlotAssignmentsOptions {
    signal?: AbortSignal;
    fetchFn?: typeof fetch;
    // Which stored version of each app to resolve. Defaults to the released one,
    // and this request is the only place the choice is ever expressed: the
    // response's `apps_origin` names the matching apps host, and every request the
    // browser makes afterwards takes its channel from that Host.
    channel?: AppChannel;
}

// A framed widget, carrying the absolute `bundle_url` to load it from. Never
// fall back to the superseded `bundle_path`. A button is drawn by the host and
// has no address, so it is never one of these.
export interface AddressableSlotAssignment extends SlotAssignmentView {
    bundle_url: string;
}

export const isAddressable = (assignment: SlotAssignmentView): assignment is AddressableSlotAssignment =>
    assignment.bundle_url !== undefined;

export interface SlotAssignments {
    // The apps host every `bundle_url` below is served from — the origin the
    // product mints a session against and the only origin whose postMessages the
    // slot bridge will trust. Undefined on a deployment or host with no derived
    // apps host, which is the documented "no widget surface" state; `assignments`
    // is then empty.
    appsOrigin: string | undefined;
    // Every framed widget here has a `bundle_url`; host-rendered ones never do.
    // Narrow with `isAddressable` before treating one as an iframe source.
    assignments: SlotAssignmentView[];
}

// Whether this widget needs an address at all. A frame is useless without one; a
// button never had one to lose. `isFramedWidgetKind` is the contract's own list,
// the one lumi-mcp-server reads to decide which assignments carry a `bundle_url`.
const isFramed = (assignment: SlotAssignmentView): boolean => isFramedWidgetKind(assignment.widget.kind);

// lumi-mcp-server's answer for a slot id it does not know: 400, with this body
// (`slotAssignmentsHandler` in src/serve/apps-platform-api.ts). The product ships
// a new slot before or after the platform, on separate release trains, and until
// the platform knows the slot no app can declare it, so the honest reading is an
// empty slot. Matched on the code AND the message prefix, so any other 400 stays a
// failure.
const UNKNOWN_SLOT_ERROR = 'invalid_request';
const UNKNOWN_SLOT_MESSAGE_PREFIX = 'unknown slotId';

async function isUnknownSlotResponse(response: Response): Promise<boolean> {
    if (response.status !== 400 || !response.headers.get('content-type')?.includes('application/json')) {
        return false;
    }
    try {
        const body: unknown = await response.json();
        if (typeof body !== 'object' || body === null) return false;
        const { error, message } = body as { error?: unknown; message?: unknown };
        return (
            error === UNKNOWN_SLOT_ERROR &&
            typeof message === 'string' &&
            message.startsWith(UNKNOWN_SLOT_MESSAGE_PREFIX)
        );
    } catch {
        return false;
    }
}

export async function fetchSlotAssignments(
    slotId: SlotId,
    { signal, fetchFn = fetch, channel = 'release' }: FetchSlotAssignmentsOptions = {},
): Promise<SlotAssignments> {
    // Release asks for nothing rather than asking for `?channel=release`: it is by
    // far the common request, the platform already defaults to it, and leaving the
    // URL untouched keeps every cache and log line between here and there reading
    // exactly as it did before drafts existed.
    const path = `/apps/_api/slots/${encodeURIComponent(slotId)}/assignments`;
    const response = await fetchFn(channel === 'release' ? path : `${path}?channel=${channel}`, {
        signal,
        headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${getToken()}`,
        },
    });

    if (await isUnknownSlotResponse(response)) {
        console.debug(`[apps] the platform does not know ${slotId} yet; showing it empty.`);
        return { appsOrigin: undefined, assignments: [] };
    }
    if (!response.ok) {
        throw new Error(`Failed to load slot assignments (${response.status})`);
    }
    const contentType = response.headers.get('content-type');
    // Only `*.luminovo.com` routes `/apps/_api`; on a tenant's custom domain the product's own catch-all answers
    // with the app's HTML. That is a host without a widget surface, the state a missing apps origin describes
    // below, not a failed request.
    if (contentType?.includes('text/html')) {
        return { appsOrigin: undefined, assignments: [] };
    }
    if (!contentType?.includes('application/json')) {
        throw new Error('Failed to load slot assignments: expected JSON response');
    }

    const parsed = SlotAssignmentsResponseSchema.safeParse(await response.json());
    if (!parsed.success) {
        throw new Error('Failed to load slot assignments: invalid response');
    }
    if (parsed.data.slot !== slotId) {
        throw new Error(`Failed to load slot assignments: response was for ${parsed.data.slot}`);
    }

    const { apps_origin: appsOrigin, assignments } = parsed.data;

    // No apps origin, no addressable widget — and nothing to say about it when the
    // slot is empty anyway. Worth one line only when widgets ARE declared, because
    // then a page that renders nothing looks like a bug rather than a host without
    // a widget surface.
    if (appsOrigin === undefined) {
        if (assignments.length > 0) {
            console.info(
                `[apps] ${assignments.length} widget(s) declare ${slotId}, but this host publishes no apps origin — none can be rendered here.`,
            );
        }
        return { appsOrigin: undefined, assignments: [] };
    }

    const renderable = assignments.filter((assignment) => !isFramed(assignment) || isAddressable(assignment));
    if (renderable.length !== assignments.length) {
        // The platform emits apps_origin and bundle_url together for a framed
        // widget, so this is drift rather than the documented degradation above.
        console.warn(
            `[apps] skipped ${assignments.length - renderable.length} framed widget(s) in ${slotId} with no bundle_url.`,
        );
    }
    return { appsOrigin, assignments: renderable };
}
